import { z } from 'zod'

/**
 * Vendor-supplied attributes, licensed for a fixed term. Mirrors
 * `ListAttributes` in the db schema — see docs/calling-list-semantics.md.
 */
export const listAttributesSchema = z.object({
  street: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
  municipality: z.string().nullable(),
  region: z.string().nullable(),
  language: z.string().nullable(),
  addressHash: z.string().nullable(),
})

export const callingListRowSchema = z.object({
  phone: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  attributes: listAttributesSchema,
  raw: z.record(z.string(), z.string()),
})

// Business-subscription and PO-box filtering is configured in the Profinder
// order itself, so there is deliberately no reason for either here — two copies
// of one rule only drift apart.
export const callingListScrubReasonSchema = z.enum([
  'robinson',
  'missing_phone',
  'duplicate_phone',
  'suppressed',
])

export const CALLING_LIST_SCRUB_REASON_LABELS: Record<
  z.infer<typeof callingListScrubReasonSchema>,
  string
> = {
  robinson: 'Robinson-kielto',
  missing_phone: 'Puhelinnumero puuttuu',
  duplicate_phone: 'Sama numero kahdesti',
  suppressed: 'Soittokielto tai väärä numero',
}

export const callingListReportSchema = z.object({
  totalRows: z.number(),
  /** Rows that survived scrubbing and will be written. */
  accepted: z.number(),
  /** Of `accepted`, how many resolve to a person we already have. */
  alreadyKnown: z.number(),
  /**
   * Of `accepted`, how many share an address with another accepted row —
   * spouses, typically. Informational: both are imported, because a second
   * number is a second chance at the household and the vendor data expires
   * anyway. Nothing yet stops the second call once the first one books; that
   * belongs on `isSameAddress` (modules/property/addressSiblings), which the
   * reminders already use and which survives the licence purge.
   */
  sameHousehold: z.number(),
  droppedByReason: z.record(callingListScrubReasonSchema, z.number()),
})

/**
 * Only the file. The import is faithful to what the vendor delivered —
 * geography is scoped upstream when ordering the list, and downstream by the
 * campaign's `callingListArea` filter. Filtering here would discard paid-for
 * rows on a typo, with nothing but a count to notice it by.
 */
export const parseCallingListInputSchema = z.object({
  content: z.string(),
})

export const importCallingListInputSchema = parseCallingListInputSchema.extend({
  sourceBatch: z.string().min(1),
  /** Licence term from the vendor's terms of use. */
  // Capped at the vendor's own term: a client must never be able to extend
  // the destruction deadline, only shorten it for a partial-term purchase.
  licenceDays: z.number().int().positive().max(60).default(60),
})

export const parseCallingListResultSchema = z.object({
  report: callingListReportSchema,
  /** A handful of accepted rows, so the operator can eyeball the mapping. */
  preview: z.array(callingListRowSchema),
})

export const importCallingListResultSchema = z.object({
  report: callingListReportSchema,
  sourceBatch: z.string(),
  licenseExpiresAt: z.string(),
  usersCreated: z.number(),
  entriesCreated: z.number(),
})

export type ListAttributes = z.infer<typeof listAttributesSchema>
export type CallingListRow = z.infer<typeof callingListRowSchema>
export type CallingListScrubReason = z.infer<
  typeof callingListScrubReasonSchema
>
export type CallingListReport = z.infer<typeof callingListReportSchema>
export type ParseCallingListResult = z.infer<
  typeof parseCallingListResultSchema
>
export type ImportCallingListResult = z.infer<
  typeof importCallingListResultSchema
>
